// Portal de instalación (Plan Completo): qué hay en el alojamiento, si está bien puesto y cómo conectarlo.
// Pensado para quien instala sin ser experto: lista lateral tipo consola de equipos + ficha de cada uno.
import React, { useState, useMemo } from "react";
import { motion } from "framer-motion";
import {
  Router, Cpu, Zap, Thermometer, Droplets, Gauge, Power, CircleHelp, CheckCircle2, AlertTriangle, XCircle,
  CircleDashed, Info, ListChecks, LayoutGrid, Cable, Settings2, Download, RefreshCw, Play, Trash2, Plus, Copy, Radio,
} from "lucide-react";
import api from "./services/api";
import useApi from "./hooks/useApi";

const C = {
  ink: "#283226", ink2: "#4B5347", ink3: "#7D8478", ink4: "#A9AEA2", paper: "#F7F7F7", line: "#E4E7E0",
  forest: "#406020", primary: "#406020", moss: "#6F8F4E", ochre: "#C08B2C", clay: "#B4532F", sky: "#3D6E8E",
};
const TONE = { good: C.moss, warn: C.ochre, bad: C.clay, info: C.sky, off: C.ink4 };
const TONE_TXT = { good: "Correcto", warn: "Revisar", bad: "Fallo", info: "Info", off: "Esperando" };
const TIPO_ICON = {
  energia_trifasica: Zap, energia_monofasica: Zap, clima: Thermometer, pulsos_agua: Droplets, nivel_agua: Gauge,
  actuador: Power, pasarela: Cpu, router: Router, desconocido: CircleHelp,
};
const CHECK_ICON = { good: CheckCircle2, warn: AlertTriangle, bad: XCircle, off: CircleDashed, info: Info };
const VAL = {
  voltage_a: ["Tensión A", "V"], voltage_b: ["Tensión B", "V"], voltage_c: ["Tensión C", "V"], voltage: ["Tensión", "V"],
  voltage_ab: ["Tensión A-B", "V"], voltage_bc: ["Tensión B-C", "V"], voltage_ca: ["Tensión C-A", "V"],
  current_a: ["Corriente A", "A"], current_b: ["Corriente B", "A"], current_c: ["Corriente C", "A"], current_n: ["Corriente N", "A"], current: ["Corriente", "A"],
  power_a: ["Potencia A", "W"], power_b: ["Potencia B", "W"], power_c: ["Potencia C", "W"], power: ["Potencia", "W"],
  power_factor: ["Factor de potencia", ""], ac_frequency: ["Frecuencia", "Hz"], energy: ["Energía acumulada", "kWh"],
  temperature: ["Temperatura", "°C"], humidity: ["Humedad", "%"], battery: ["Batería", "%"], linkquality: ["Señal Zigbee", "LQI"],
  pulses: ["Pulsos", ""], liquid_level_percent: ["Nivel", "%"], liquid_depth: ["Profundidad", "m"], state: ["Estado", ""],
  modbus_ok: ["Medidor responde", ""], lecturas: ["Lecturas", ""], medidor_ip: ["IP del medidor", ""], estado: ["Estado", ""],
};
const PRESETS = {
  "Pickdata + PM8000": [{ modelo: "rutx50" }, { modelo: "pickdata" }, { modelo: "pm8000" }],
  "Zigbee completo": [{ modelo: "rutx50" }, { modelo: "revpi" }, { modelo: "slzb06" }, { modelo: "spm02" }, { modelo: "snzb02d" }, { modelo: "snzb02d" },
    { modelo: "zipulses" }, { modelo: "aqara_t1" }, { modelo: "zbmini" }],
};

const mono = { fontFamily: "var(--font-mono)" };
const card = { background: "#fff", border: `1px solid ${C.line}`, borderRadius: 16 };
const btn = (primary, danger) => ({
  display: "inline-flex", alignItems: "center", gap: 7, padding: "8px 16px", borderRadius: 999, cursor: "pointer", fontSize: 13, fontWeight: 600,
  fontFamily: "var(--font-sans)", border: `1px solid ${danger ? C.clay : primary ? C.primary : C.line}`,
  background: primary ? C.primary : "#fff", color: primary ? "#fff" : danger ? C.clay : C.ink,
});
const input = { width: "100%", boxSizing: "border-box", padding: "8px 10px", border: `1px solid ${C.line}`, borderRadius: 10, fontSize: 14, fontFamily: "var(--font-sans)", color: C.ink, background: "#fff" };

function Mark({ tone, size = 9, dashed }) {
  return <span aria-hidden style={{ width: size, height: size, borderRadius: 1.5, flexShrink: 0, display: "inline-block",
    background: dashed ? "transparent" : TONE[tone] || TONE.off, border: dashed ? `1.5px dashed ${C.ink4}` : "none" }} />;
}
function Eyebrow({ children, style }) {
  return <div style={{ ...mono, fontSize: 10.5, letterSpacing: 1.1, textTransform: "uppercase", color: C.ink3, ...style }}>{children}</div>;
}
function hace(min) {
  if (min === null || min === undefined) return "nunca";
  if (min < 1) return "ahora";
  if (min < 60) return `hace ${min} min`;
  if (min < 2880) return `hace ${Math.round(min / 60)} h`;
  return `hace ${Math.round(min / 1440)} días`;
}
function principal(d) {
  const v = d.valores || {};
  if (d.tipo === "energia_trifasica" || d.tipo === "energia_monofasica") return v.power !== undefined ? `${v.power} W` : "—";
  if (d.tipo === "clima") return v.temperature !== undefined ? `${v.temperature} °C${v.humidity !== undefined ? ` · ${v.humidity} %` : ""}` : "—";
  if (d.tipo === "nivel_agua") return v.liquid_level_percent !== undefined ? `${v.liquid_level_percent} %` : "—";
  if (d.tipo === "pulsos_agua") return v.pulses !== undefined ? `${v.pulses} pulsos` : "—";
  if (d.tipo === "actuador") return v.state || "—";
  if (d.tipo === "pasarela") return v.edge === "zigbee2mqtt" ? (v.estado || "—") : v.modbus_ok === undefined ? "—" : v.modbus_ok ? "medidor OK" : "sin medidor";
  return "—";
}
const nombreDe = (d) => d.alias || d.device;

// ── Vistas ──────────────────────────────────────────────────────────────────
function Resumen({ data, open }) {
  const r = data.resumen;
  const tiles = [
    ["Equipos", r.equipos, C.primary], ["Correctos", r.ok, C.moss], ["Para revisar", r.avisos + r.errores, r.errores ? C.clay : C.ochre], ["Esperando", r.esperando, C.ink4],
  ];
  return (
    <div>
      <div className="er-install-tiles" style={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0,1fr))", gap: 12, marginBottom: 22 }}>
        {tiles.map(([l, v, col]) => (
          <div key={l} style={{ ...card, padding: "14px 16px", borderBottom: `2px solid ${col}` }}>
            <Eyebrow>{l}</Eyebrow>
            <div className="er-figure" style={{ fontFamily: "var(--font-serif)", fontSize: 30, color: C.ink, marginTop: 4 }}>{v}</div>
          </div>
        ))}
      </div>
      <div style={{ ...card, overflow: "hidden" }}>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13.5 }}>
            <thead><tr style={{ background: C.paper }}>
              {["Estado", "Equipo", "Tipo", "Lectura", "Último dato", "Comprobación"].map(h => (
                <th key={h} style={{ ...mono, textAlign: "left", fontWeight: 500, fontSize: 10.5, letterSpacing: 0.9, textTransform: "uppercase", color: C.ink3, padding: "10px 14px", whiteSpace: "nowrap" }}>{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {data.devices.map(d => {
                const peor = d.checks.find(k => k.tone === d.tone) || d.checks[0] || {};
                return (
                  <tr key={d.device} onClick={() => open("dev:" + d.device)} className="er-install-row" style={{ borderTop: `1px solid ${C.line}`, cursor: "pointer" }}>
                    <td style={{ padding: "11px 14px", whiteSpace: "nowrap" }}><span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}><Mark tone={d.tone} /><span style={{ ...mono, fontSize: 10.5, letterSpacing: 0.7, textTransform: "uppercase", color: C.ink2 }}>{TONE_TXT[d.tone]}</span></span></td>
                    <td style={{ padding: "11px 14px" }}><div style={{ fontWeight: 600, color: C.ink }}>{nombreDe(d)}</div>{d.modelo_nombre && <div style={{ fontSize: 12, color: C.ink3 }}>{d.modelo_nombre}</div>}</td>
                    <td style={{ padding: "11px 14px", color: C.ink2, whiteSpace: "nowrap" }}>{d.tipo_nombre}</td>
                    <td style={{ padding: "11px 14px", ...mono, color: C.ink, whiteSpace: "nowrap" }}>{principal(d)}</td>
                    <td style={{ padding: "11px 14px", color: C.ink2, whiteSpace: "nowrap" }}>{hace(d.minutos)}</td>
                    <td style={{ padding: "11px 14px", color: d.tone === "good" ? C.ink2 : TONE[d.tone] }}>{peor.label || "—"}</td>
                  </tr>
                );
              })}
              {data.pendientes.map(p => (
                <tr key={"p" + p.idx} onClick={() => open("pend:" + p.idx)} className="er-install-row" style={{ borderTop: `1px solid ${C.line}`, cursor: "pointer", color: C.ink3 }}>
                  <td style={{ padding: "11px 14px" }}><span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}><Mark dashed /><span style={{ ...mono, fontSize: 10.5, letterSpacing: 0.7, textTransform: "uppercase" }}>Esperando</span></span></td>
                  <td style={{ padding: "11px 14px" }}>{p.nombre}</td>
                  <td style={{ padding: "11px 14px" }}>{data.tipos[p.tipo] || p.tipo}</td>
                  <td style={{ padding: "11px 14px" }}>—</td><td style={{ padding: "11px 14px" }}>nunca</td>
                  <td style={{ padding: "11px 14px" }}>Previsto en el kit, aún no ha conectado</td>
                </tr>
              ))}
              {data.devices.length === 0 && data.pendientes.length === 0 && (
                <tr><td colSpan={6} style={{ padding: 28, textAlign: "center", color: C.ink3 }}>
                  Aún no ha llegado ningún equipo. Conecta la pasarela (sección «Pasarela y conexión») y los equipos aparecerán aquí solos.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function Asistente({ data, open }) {
  const destino = { pasarela: "conexion", equipos: "resumen", lecturas: "resumen", potencia: "ajustes", avisos: "ajustes",
    actuadores: (() => { const d = data.devices.find(x => x.tipo === "actuador" && !x.probado_ts); return d ? "dev:" + d.device : "resumen"; })() };
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {data.pasos.map((p, i) => {
        const Ico = p.ok ? CheckCircle2 : p.opcional ? CircleDashed : AlertTriangle;
        const col = p.ok ? C.moss : p.opcional ? C.ink4 : C.ochre;
        return (
          <div key={p.id} style={{ ...card, padding: "14px 18px", display: "flex", gap: 14, alignItems: "flex-start" }}>
            <div style={{ ...mono, fontSize: 12, color: C.ink4, width: 22, paddingTop: 2 }}>{String(i + 1).padStart(2, "0")}</div>
            <Ico size={20} strokeWidth={1.7} color={col} style={{ flexShrink: 0, marginTop: 1 }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 600, color: C.ink, fontSize: 14.5 }}>{p.label}{p.opcional && <span style={{ fontWeight: 400, color: C.ink3 }}> · opcional</span>}</div>
              {p.detalle && <div style={{ fontSize: 13, color: C.ink2, marginTop: 3, lineHeight: 1.45 }}>{p.detalle}</div>}
            </div>
            {!p.ok && destino[p.id] && <button onClick={() => open(destino[p.id])} style={{ ...btn(false), padding: "6px 12px", fontSize: 12.5 }}>Ir</button>}
          </div>
        );
      })}
    </div>
  );
}

function Equipo({ d, data, isAdmin, acc, refetch, open }) {
  const [form, setForm] = useState({ alias: d.alias || "", zona: d.zona || "", modelo: d.modelo || "", tipo: d.tipo, cups: d.cups || "" });
  const sums = (data.ajustes && data.ajustes.suministros) || [];
  const pideCups = sums.length > 1 && (form.tipo === "energia_trifasica" || form.tipo === "energia_monofasica");
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [prueba, setPrueba] = useState(null); // null | 'enviando' | 'preguntar'
  const Ico = TIPO_ICON[d.tipo] || CircleHelp;
  const modelos = Object.entries(data.catalogo).filter(([, m]) => form.tipo === "desconocido" || m.tipo === form.tipo);
  const guardar = async (body, ok) => {
    setBusy(true); setMsg(null);
    try { await api.putDevice(acc.locationID, d.device, body); setMsg({ ok: true, t: ok || "Guardado" }); refetch(); }
    catch (e) { setMsg({ ok: false, t: e.status === 403 ? "Solo un administrador puede cambiar esto." : e.message }); }
    finally { setBusy(false); }
  };
  const probar = async () => {
    if (!d.actuador) { setMsg({ ok: false, t: "Guarda primero el equipo como actuador." }); return; }
    setPrueba("enviando"); setMsg(null);
    try {
      await api.postActuator(acc.locationID, d.actuador, "ON");
      await new Promise(r => setTimeout(r, 5000));
      await api.postActuator(acc.locationID, d.actuador, "OFF");
      setPrueba("preguntar");
    } catch (e) { setPrueba(null); setMsg({ ok: false, t: e.message }); }
  };
  const valores = Object.entries(d.valores || {}).filter(([k]) => !["last_seen", "source", "edge", "medidor", "ultima_lectura", "error", "aviso"].includes(k));
  return (
    <div>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 14, marginBottom: 18, flexWrap: "wrap" }}>
        <span style={{ width: 46, height: 46, borderRadius: 12, background: "#EBF2E4", color: C.primary, display: "inline-flex", alignItems: "center", justifyContent: "center" }}><Ico size={22} strokeWidth={1.6} /></span>
        <div style={{ flex: 1, minWidth: 200 }}>
          <div style={{ fontFamily: "var(--font-serif)", fontSize: 24, fontWeight: 700, color: C.primary, lineHeight: 1.15 }}>{nombreDe(d)}</div>
          <div style={{ fontSize: 13, color: C.ink3, marginTop: 3 }}>{[d.modelo_nombre, d.tipo_nombre, d.via, d.zona].filter(Boolean).join(" · ")}</div>
        </div>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 8, border: `1px solid ${C.line}`, borderRadius: 4, padding: "5px 10px" }}>
          <Mark tone={d.tone} /><span style={{ ...mono, fontSize: 10.5, letterSpacing: 0.8, textTransform: "uppercase", color: C.ink2 }}>{TONE_TXT[d.tone]}</span>
        </span>
      </div>

      <div className="er-install-two" style={{ display: "grid", gridTemplateColumns: "minmax(0,1.1fr) minmax(0,1fr)", gap: 16 }}>
        <div style={{ ...card, padding: 18 }}>
          <Eyebrow style={{ marginBottom: 10 }}>Comprobaciones</Eyebrow>
          {d.checks.map((k, i) => {
            const I2 = CHECK_ICON[k.tone] || Info;
            return (
              <div key={i} style={{ display: "flex", gap: 10, padding: "8px 0", borderTop: i ? `1px solid ${C.line}` : "none" }}>
                <I2 size={17} strokeWidth={1.8} color={TONE[k.tone]} style={{ flexShrink: 0, marginTop: 1 }} />
                <div><div style={{ fontSize: 14, color: C.ink, fontWeight: 500 }}>{k.label}</div>{k.detalle && <div style={{ fontSize: 12.5, color: C.ink2, marginTop: 2, lineHeight: 1.45 }}>{k.detalle}</div>}</div>
              </div>
            );
          })}
          {d.actuador && (
            <div style={{ marginTop: 12, paddingTop: 12, borderTop: `1px solid ${C.line}` }}>
              {prueba === "preguntar" ? (
                <div>
                  <div style={{ fontSize: 13.5, color: C.ink, marginBottom: 8 }}>Se ha enviado activar y, 5 s después, desactivar. ¿Se ha movido de verdad?</div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <button style={btn(true)} onClick={() => { setPrueba(null); guardar({ probado: true }, "Marcado como probado"); }}>Sí, funciona</button>
                    <button style={btn(false)} onClick={() => { setPrueba(null); setMsg({ ok: false, t: "Revisa la alimentación del actuador y que esté emparejado; luego vuelve a probar." }); }}>No</button>
                  </div>
                </div>
              ) : (
                <button style={btn(true)} disabled={prueba === "enviando"} onClick={probar}>
                  <Play size={14} strokeWidth={2} />{prueba === "enviando" ? "Probando… (activar, esperar, desactivar)" : "Probar"}
                </button>
              )}
            </div>
          )}
        </div>

        <div style={{ ...card, padding: 18 }}>
          <Eyebrow style={{ marginBottom: 10 }}>Últimas lecturas {d.desde_archivo && "· del archivo"}</Eyebrow>
          {valores.length === 0 ? <div style={{ color: C.ink3, fontSize: 13.5 }}>Sin datos todavía.</div> : (
            <div style={{ display: "grid", gridTemplateColumns: "1fr auto", rowGap: 6, columnGap: 12, fontSize: 13.5 }}>
              {valores.map(([k, v]) => (
                <React.Fragment key={k}>
                  <div style={{ color: C.ink2 }}>{VAL[k] ? VAL[k][0] : k}</div>
                  <div style={{ ...mono, color: C.ink, textAlign: "right" }}>{typeof v === "boolean" ? (v ? "sí" : "no") : String(v)}{VAL[k] && VAL[k][1] ? " " + VAL[k][1] : ""}</div>
                </React.Fragment>
              ))}
            </div>
          )}
          <div style={{ marginTop: 14, paddingTop: 12, borderTop: `1px solid ${C.line}`, display: "grid", gridTemplateColumns: "auto 1fr", rowGap: 4, columnGap: 12, fontSize: 12.5, color: C.ink3 }}>
            <span>Último dato</span><span style={{ ...mono, color: C.ink2 }}>{d.last_seen ? new Date(d.last_seen).toLocaleString("es-ES") : "—"} ({hace(d.minutos)})</span>
            <span>Visto por primera vez</span><span style={{ ...mono, color: C.ink2 }}>{d.first_seen ? new Date(d.first_seen).toLocaleString("es-ES") : "—"}</span>
            <span>Topic MQTT</span><span style={{ ...mono, color: C.ink2, wordBreak: "break-all" }}>easyrural/{data.conexion.prefijo || acc.locationID}/zigbee2mqtt/{d.device}</span>
          </div>
        </div>
      </div>

      {d.consejo && (
        <div style={{ marginTop: 16, padding: "12px 16px", borderLeft: `2px solid ${C.sky}`, background: "#EEF3F6", borderRadius: 4, fontSize: 13.5, color: C.ink, lineHeight: 1.5 }}>
          <Eyebrow style={{ marginBottom: 4, color: C.sky }}>Al instalar</Eyebrow>{d.consejo}
        </div>
      )}

      <div style={{ ...card, padding: 18, marginTop: 16 }}>
        <Eyebrow style={{ marginBottom: 12 }}>Ficha del equipo {!isAdmin && "· solo lectura"}</Eyebrow>
        <div className="er-install-form" style={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0,1fr))", gap: 12 }}>
          <label style={{ fontSize: 12.5, color: C.ink3 }}>Nombre visible<input disabled={!isAdmin} style={{ ...input, marginTop: 4 }} value={form.alias} placeholder={d.device} onChange={e => setForm({ ...form, alias: e.target.value })} /></label>
          <label style={{ fontSize: 12.5, color: C.ink3 }}>Dónde está<input disabled={!isAdmin} style={{ ...input, marginTop: 4 }} value={form.zona} placeholder="Cuadro general, salón…" onChange={e => setForm({ ...form, zona: e.target.value })} /></label>
          <label style={{ fontSize: 12.5, color: C.ink3 }}>Tipo
            <select disabled={!isAdmin} style={{ ...input, marginTop: 4 }} value={form.tipo} onChange={e => setForm({ ...form, tipo: e.target.value, modelo: "" })}>
              {Object.entries(data.tipos).filter(([k]) => k !== "router").map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select></label>
          <label style={{ fontSize: 12.5, color: C.ink3 }}>Modelo
            <select disabled={!isAdmin} style={{ ...input, marginTop: 4 }} value={form.modelo} onChange={e => setForm({ ...form, modelo: e.target.value })}>
              <option value="">Sin indicar</option>
              {modelos.map(([k, m]) => <option key={k} value={k}>{m.nombre}</option>)}
            </select></label>
          {pideCups && (
            <label style={{ fontSize: 12.5, color: C.ink3, gridColumn: "1 / -1" }}>Suministro que mide (hay {sums.length} contratos de luz)
              <select disabled={!isAdmin} style={{ ...input, marginTop: 4, ...mono }} value={form.cups} onChange={e => setForm({ ...form, cups: e.target.value })}>
                <option value="">Sin indicar</option>
                {sums.map(x => <option key={x.cups} value={x.cups}>{x.cups} · {x.kw ?? "?"} kW contratados</option>)}
              </select>
              <span style={{ fontSize: 11.5 }}>El CUPS viene en la factura o en el contador del cuadro. Sirve para avisar con la potencia de ese contrato.</span>
            </label>
          )}
        </div>
        {isAdmin && (
          <div style={{ display: "flex", gap: 8, marginTop: 14, flexWrap: "wrap", alignItems: "center" }}>
            <button style={btn(true)} disabled={busy} onClick={() => guardar({ alias: form.alias, zona: form.zona, modelo: form.modelo || null, ...(form.tipo !== d.tipo ? { tipo: form.tipo } : {}), ...(pideCups ? { cups: form.cups || null } : {}) })}>Guardar</button>
            <button style={btn(false, true)} disabled={busy} onClick={() => {
              if (window.confirm(`¿Olvidar «${nombreDe(d)}»? Si vuelve a enviar datos se dará de alta otra vez.`)) guardar({ olvidar: true }, "Equipo olvidado").then(() => open("resumen"));
            }}><Trash2 size={14} strokeWidth={1.8} />Olvidar equipo</button>
            {msg && <span style={{ fontSize: 13, color: msg.ok ? C.moss : C.clay, fontWeight: 600 }}>{msg.t}</span>}
          </div>
        )}
        {!isAdmin && msg && <div style={{ marginTop: 10, fontSize: 13, color: msg.ok ? C.moss : C.clay, fontWeight: 600 }}>{msg.t}</div>}
      </div>
    </div>
  );
}

function Pendiente({ p, data }) {
  const zigbee = data.catalogo[p.modelo]?.via === "Zigbee";
  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 18 }}>
        <span style={{ width: 46, height: 46, borderRadius: 12, border: `1.5px dashed ${C.ink4}`, display: "inline-flex", alignItems: "center", justifyContent: "center", color: C.ink3 }}>
          {React.createElement(TIPO_ICON[p.tipo] || CircleHelp, { size: 22, strokeWidth: 1.6 })}
        </span>
        <div>
          <div style={{ fontFamily: "var(--font-serif)", fontSize: 24, color: C.ink }}>{p.nombre}</div>
          <div style={{ fontSize: 13, color: C.ink3 }}>{[data.tipos[p.tipo], p.zona].filter(Boolean).join(" · ")}</div>
        </div>
      </div>
      <div style={{ ...card, padding: 18 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
          <motion.span animate={{ opacity: [0.35, 1, 0.35] }} transition={{ duration: 1.8, repeat: Infinity }}><Mark tone="off" size={10} /></motion.span>
          <span style={{ fontWeight: 600, color: C.ink }}>Esperando el primer mensaje…</span>
        </div>
        <ol style={{ margin: 0, paddingLeft: 20, color: C.ink2, fontSize: 14, lineHeight: 1.7 }}>
          {p.tipo === "pasarela" ? <>
            <li>Conecta la pasarela por cable a un puerto LAN del router y dale corriente.</li>
            <li>Sigue los pasos de «Pasarela y conexión» para cargarle la configuración.</li>
            <li>En un minuto debería aparecer aquí sola.</li>
          </> : zigbee ? <>
            <li>Comprueba que la pasarela Zigbee está en marcha (arriba del todo de la lista, en verde).</li>
            <li>Activa «Permitir que se unan» en zigbee2mqtt.</li>
            <li>Pon el equipo en modo emparejamiento (normalmente, mantener pulsado su botón unos 5 s hasta que parpadee; mira su manual).</li>
            <li>En cuanto envíe su primer dato aparecerá aquí y se comprobará solo.</li>
          </> : <>
            <li>Conecta el equipo y su comunicación ({data.catalogo[p.modelo]?.via || "ver manual"}).</li>
            <li>Configura que publique en el topic del alojamiento (ver «Pasarela y conexión»).</li>
            <li>En cuanto envíe su primer dato aparecerá aquí y se comprobará solo.</li>
          </>}
        </ol>
      </div>
      {p.consejo && (
        <div style={{ marginTop: 16, padding: "12px 16px", borderLeft: `2px solid ${C.sky}`, background: "#EEF3F6", borderRadius: 4, fontSize: 13.5, color: C.ink, lineHeight: 1.5 }}>
          <Eyebrow style={{ marginBottom: 4, color: C.sky }}>Al instalar</Eyebrow>{p.consejo}
        </div>
      )}
    </div>
  );
}

function Copiable({ label, value }) {
  const [ok, setOk] = useState(false);
  return (
    <div style={{ display: "grid", gridTemplateColumns: "150px 1fr auto", gap: 10, alignItems: "center", padding: "8px 0", borderTop: `1px solid ${C.line}` }} className="er-install-copy">
      <span style={{ fontSize: 13, color: C.ink3 }}>{label}</span>
      <span style={{ ...mono, fontSize: 13, color: C.ink, wordBreak: "break-all" }}>{value || "—"}</span>
      {value ? <button aria-label={"Copiar " + label} onClick={() => { navigator.clipboard?.writeText(String(value)); setOk(true); setTimeout(() => setOk(false), 1500); }}
        style={{ ...btn(false), padding: "4px 8px", fontSize: 12 }}>{ok ? "Copiado" : <Copy size={13} strokeWidth={1.8} />}</button> : <span />}
    </div>
  );
}

function Conexion({ data, acc }) {
  const pas = data.ajustes.pasarela || {};
  const [ip, setIp] = useState(pas.ip_medidor || "192.168.1.13");
  const [unit, setUnit] = useState(pas.unit_id || 1);
  const [err, setErr] = useState(null);
  const k = data.conexion;
  const descargar = async () => {
    setErr(null);
    try {
      const r = await api.getInstallKit(acc.locationID, { ip, unit });
      const blob = new Blob([JSON.stringify(r.flujo, null, 2)], { type: "application/json" });
      const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = r.archivo; a.click();
      setTimeout(() => URL.revokeObjectURL(a.href), 2000);
    } catch (e) { setErr(e.message); }
  };
  const paso = (n, t, body) => (
    <div style={{ display: "flex", gap: 14, padding: "14px 0", borderTop: n > 1 ? `1px solid ${C.line}` : "none" }}>
      <div style={{ ...mono, fontSize: 12, color: C.ink4, width: 22, paddingTop: 2 }}>{String(n).padStart(2, "0")}</div>
      <div style={{ flex: 1, minWidth: 0 }}><div style={{ fontWeight: 600, color: C.ink, marginBottom: 4 }}>{t}</div><div style={{ fontSize: 13.5, color: C.ink2, lineHeight: 1.55 }}>{body}</div></div>
    </div>
  );
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div style={{ ...card, padding: 18 }}>
        <Eyebrow style={{ marginBottom: 6 }}>Datos de conexión del alojamiento</Eyebrow>
        <Copiable label="Kit IoT" value={k.kit_iot || "sin kit asignado (usa el nombre del alojamiento)"} />
        <Copiable label="Servidor MQTT" value={k.broker} />
        <Copiable label="Puerto (TLS)" value={k.puerto} />
        <Copiable label="Usuario sugerido" value={k.usuario_sugerido} />
        <Copiable label="Permiso del usuario" value={k.permiso} />
        <Copiable label="Topic de los equipos" value={k.topic} />
        <div style={{ fontSize: 12.5, color: C.ink3, marginTop: 8 }}>La contraseña no se muestra nunca aquí: se crea en la consola de HiveMQ y se escribe solo en la pasarela.</div>
      </div>

      <div style={{ ...card, padding: "4px 18px" }}>
        {paso(1, "Router con SIM", "Enciende el router, comprueba en el RMS que tiene cobertura y conecta la pasarela por cable a un puerto LAN.")}
        {paso(2, "Usuario MQTT del alojamiento", <>En la consola de HiveMQ Cloud → Access Management, crea el usuario <b style={mono}>{k.usuario_sugerido}</b> con permiso solo sobre <b style={mono}>{k.permiso}</b>.</>)}
        {paso(3, "Pasarela Pickdata con medidor por Modbus", <>
          <div className="er-install-kit" style={{ display: "grid", gridTemplateColumns: "1fr 110px auto", gap: 10, alignItems: "end", margin: "6px 0 8px" }}>
            <label style={{ fontSize: 12.5, color: C.ink3 }}>IP del medidor<input style={{ ...input, marginTop: 4 }} value={ip} onChange={e => setIp(e.target.value)} /></label>
            <label style={{ fontSize: 12.5, color: C.ink3 }}>Unidad Modbus<input style={{ ...input, marginTop: 4 }} type="number" min={1} max={247} value={unit} onChange={e => setUnit(e.target.value)} /></label>
            <button style={btn(true)} onClick={descargar}><Download size={15} strokeWidth={1.8} />Descargar flujo</button>
          </div>
          En el Node-RED del Pickdata: instala <span style={mono}>node-red-contrib-modbus</span>, importa el archivo, abre el nodo «HiveMQ EasyRural», escribe el usuario y la contraseña del paso 2, y despliega.
          El flujo ya lleva el alojamiento y el servidor puestos. Si el medidor no da valores razonables, no publica nada y lo avisa aquí.
          {err && <div style={{ color: C.clay, marginTop: 6 }}>{err}</div>}
        </>)}
        {paso(4, "O bien: Revolution Pi con Zigbee", <>
          Copia el edge-kit a la Revolution Pi y ejecuta:
          <div style={{ ...mono, fontSize: 12.5, background: C.paper, border: `1px solid ${C.line}`, borderRadius: 10, padding: "8px 10px", marginTop: 6, wordBreak: "break-all" }}>
            ./setup.sh {k.prefijo || acc.locationID} {k.usuario_sugerido} &lt;contraseña&gt; &lt;ip_SLZB-06&gt;
          </div>
        </>)}
        {paso(5, "Comprobar", "Vuelve a «Resumen»: la pasarela debe salir en verde en menos de un minuto y cada equipo en cuanto mande su primer dato.")}
      </div>
    </div>
  );
}

function Ajustes({ data, acc, isAdmin, refetch }) {
  const a = data.ajustes;
  const [f, setF] = useState({
    potencia: a.potencia_manual || "", lpp: a.litros_por_pulso || "",
    radio: a.deposito?.radio_cm || "", altura: a.deposito?.altura_cm || "",
    kit: (data.kit || []).map(k => ({ ...k })),
    kitIot: a.kit_iot || "",
  });
  const [msg, setMsg] = useState(null);
  const cat = data.catalogo;
  const guardar = async () => {
    setMsg(null);
    try {
      const r = await api.putInstall(acc.locationID, {
        potencia_contratada: f.potencia === "" ? null : Number(String(f.potencia).replace(",", ".")),
        litros_por_pulso: f.lpp === "" ? null : Number(String(f.lpp).replace(",", ".")),
        deposito: f.radio && f.altura ? { radio_cm: Number(f.radio), altura_cm: Number(f.altura) } : null,
        kit: f.kit.filter(k => k.modelo),
        ...((f.kitIot || "").trim().toUpperCase() !== (a.kit_iot || "") ? { kit_iot: f.kitIot.trim() } : {}),
      });
      setMsg({ ok: true, t: r && r.kit_movido_desde ? `Guardado. El kit y sus equipos se han movido desde ${r.kit_movido_desde}.` : "Guardado" }); refetch();
    } catch (e) { setMsg({ ok: false, t: e.status === 403 ? "Solo un administrador puede cambiar esto." : (e.body && e.body.detalle) || e.message }); }
  };
  const lbl = { fontSize: 12.5, color: C.ink3 };
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div style={{ ...card, padding: 18, borderLeft: `2px solid ${C.primary}` }}>
        <Eyebrow style={{ marginBottom: 8 }}>Kit IoT instalado aquí</Eyebrow>
        <div style={{ fontSize: 13.5, color: C.ink2, lineHeight: 1.5, marginBottom: 12 }}>
          Cada kit (router + pasarela + equipos) tiene un nombre fijo, por ejemplo <b style={mono}>KIT01</b>, y la pasarela publica siempre con ese nombre.
          Para llevar el kit a otro alojamiento no hay que tocar nada en la casa: escribe aquí su nombre y guarda. Sus equipos se mueven con él.
        </div>
        <div style={{ display: "flex", gap: 10, alignItems: "flex-end", flexWrap: "wrap" }}>
          <label style={{ ...lbl, flex: "0 1 220px" }}>Nombre del kit
            <input disabled={!isAdmin} style={{ ...input, marginTop: 4, ...mono, textTransform: "uppercase" }} value={f.kitIot} placeholder="KIT01" onChange={e => setF({ ...f, kitIot: e.target.value })} /></label>
          <span style={{ fontSize: 12.5, color: C.ink3, paddingBottom: 9 }}>{a.kit_iot ? `Ahora: ${a.kit_iot}` : "Sin kit: los equipos publican con el nombre del alojamiento."}</span>
        </div>
      </div>

      <div style={{ ...card, padding: 18 }}>
        <Eyebrow style={{ marginBottom: 12 }}>Datos del alojamiento</Eyebrow>
        <div className="er-install-form" style={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0,1fr))", gap: 12 }}>
          <label style={lbl}>Potencia contratada (kW)
            <input disabled={!isAdmin || a.potencia_fuente === "datadis"} style={{ ...input, marginTop: 4 }} value={a.potencia_fuente === "datadis" ? a.potencia_contratada_kw : f.potencia} onChange={e => setF({ ...f, potencia: e.target.value })} placeholder="p. ej. 5,75" />
            {a.potencia_fuente === "datadis" && <span style={{ fontSize: 11.5 }}>Del contrato en Datadis</span>}
          </label>
          <label style={lbl}>Litros por pulso (contador)<input disabled={!isAdmin} style={{ ...input, marginTop: 4 }} value={f.lpp} onChange={e => setF({ ...f, lpp: e.target.value })} placeholder="p. ej. 1" /></label>
          <label style={lbl}>Radio del depósito (cm)<input disabled={!isAdmin} style={{ ...input, marginTop: 4 }} value={f.radio} onChange={e => setF({ ...f, radio: e.target.value })} /></label>
          <label style={lbl}>Altura útil del depósito (cm)<input disabled={!isAdmin} style={{ ...input, marginTop: 4 }} value={f.altura} onChange={e => setF({ ...f, altura: e.target.value })} /></label>
        </div>
      </div>

      <div style={{ ...card, padding: 18 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 10 }}>
          <Eyebrow>Kit previsto para este alojamiento</Eyebrow>
          {isAdmin && <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {Object.keys(PRESETS).map(n => <button key={n} style={{ ...btn(false), padding: "5px 10px", fontSize: 12 }} onClick={() => setF({ ...f, kit: PRESETS[n].map(k => ({ ...k })) })}>{n}</button>)}
          </div>}
        </div>
        <div style={{ fontSize: 13, color: C.ink2, marginBottom: 10 }}>Lo que se va a instalar. Así la lista marca lo que aún no ha conectado.</div>
        {f.kit.length === 0 && <div style={{ color: C.ink3, fontSize: 13.5, padding: "8px 0" }}>Sin kit apuntado.</div>}
        {f.kit.map((k, i) => (
          <div key={i} className="er-install-kitrow" style={{ display: "grid", gridTemplateColumns: "minmax(0,1.4fr) minmax(0,1fr) auto", gap: 8, padding: "6px 0", borderTop: `1px solid ${C.line}` }}>
            <select disabled={!isAdmin} style={input} value={k.modelo} onChange={e => { const kit = [...f.kit]; kit[i] = { ...k, modelo: e.target.value }; setF({ ...f, kit }); }}>
              {Object.entries(cat).map(([id, m]) => <option key={id} value={id}>{m.nombre}</option>)}
            </select>
            <input disabled={!isAdmin} style={input} value={k.zona || ""} placeholder={cat[k.modelo]?.zona || "Ubicación"} onChange={e => { const kit = [...f.kit]; kit[i] = { ...k, zona: e.target.value }; setF({ ...f, kit }); }} />
            {isAdmin ? <button aria-label="Quitar" style={{ ...btn(false), padding: "6px 9px" }} onClick={() => setF({ ...f, kit: f.kit.filter((_, j) => j !== i) })}><Trash2 size={14} strokeWidth={1.8} /></button> : <span />}
          </div>
        ))}
        {isAdmin && <button style={{ ...btn(false), marginTop: 10 }} onClick={() => setF({ ...f, kit: [...f.kit, { modelo: "snzb02d" }] })}><Plus size={14} strokeWidth={2} />Añadir equipo</button>}
      </div>

      {isAdmin ? (
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <button style={btn(true)} onClick={guardar}>Guardar ajustes</button>
          {msg && <span style={{ fontSize: 13, color: msg.ok ? C.moss : C.clay, fontWeight: 600 }}>{msg.t}</span>}
        </div>
      ) : <div style={{ fontSize: 13, color: C.ink3 }}>Solo lectura: los ajustes los cambia EasyRural.</div>}
      <div style={{ fontSize: 12.5, color: C.ink3 }}>Avisos por Telegram al propietario: {a.telegram ? "configurados" : "aún no (de momento van al canal de EasyRural)"}.</div>
    </div>
  );
}


const EVENTO = { device_joined: "Se ha unido a la red", device_interview: "Identificando el equipo", device_announce: "Se ha anunciado", device_leave: "Ha salido de la red", device_renamed: "Renombrado" };
function Emparejar({ data, acc, isAdmin, refetch }) {
  const z = data.zigbee || {};
  const zp = data.devices.find(d => d.device === "zigbee2mqtt");
  const conocido = !!(zp || z.version || z.prefijo);
  const online = zp ? (zp.valores || {}).estado === "online" : !!z.version;
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [now, setNow] = useState(Date.now());
  React.useEffect(() => { const t = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(t); }, []);
  const quedan = z.permit_join && z.permit_join_end ? Math.max(0, Math.round((z.permit_join_end - now) / 1000)) : 0;
  const abierto = !!z.permit_join && quedan > 0;
  React.useEffect(() => { if (!abierto) return undefined; const t = setInterval(refetch, 3000); return () => clearInterval(t); }, [abierto, refetch]);
  const pedir = async seg => {
    setBusy(true); setMsg(null);
    try {
      await api.postZigbeePermit(acc.locationID, seg);
      setMsg({ ok: true, t: seg ? "Orden enviada. La red se abre en unos segundos." : "Orden enviada para cerrar la red." });
      setTimeout(refetch, 2500); setTimeout(refetch, 6000);
    } catch (e) { setMsg({ ok: false, t: e.status === 403 ? "Solo un administrador puede emparejar equipos." : e.message }); }
    finally { setBusy(false); }
  };
  const eventos = (z.eventos || []).filter(e => e.ts > now - 30 * 60000);
  const hace2 = t => { const v = Math.max(0, Math.round((now - t) / 1000)); return v < 60 ? `hace ${v} s` : `hace ${Math.round(v / 60)} min`; };
  const mmss = t => `${Math.floor(t / 60)}:${String(t % 60).padStart(2, "0")}`;
  if (!conocido) {
    return (
      <div style={{ ...card, padding: 18, fontSize: 14, color: C.ink2, lineHeight: 1.55 }}>
        Este alojamiento todavía no tiene una pasarela Zigbee conectada. Conecta el coordinador SLZB-06 y zigbee2mqtt (ver «Pasarela y conexión») y, en cuanto se anuncie, aquí aparecerá el botón para emparejar.
      </div>
    );
  }
  const pasos = [
    ["Abre la red", "Pulsa «Permitir emparejar 2 minutos». La red Zigbee acepta equipos nuevos durante ese tiempo y se cierra sola."],
    ["Pon el equipo en modo emparejamiento", "Normalmente es mantener pulsado su botón unos 5 segundos hasta que la luz parpadee. En algunos equipos hay que apagar y encender tres veces seguidas: si no entra, mira su manual."],
    ["Espera a que aparezca", "En menos de un minuto sale aquí debajo y en la lista de equipos, ya clasificado (energía, clima, agua o actuador)."],
    ["Ponle nombre", "Abre su ficha, escribe un nombre que se entienda («Salón», «Válvula entrada») y dónde está. Si es un actuador, pulsa «Probar»."],
  ];
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div style={{ ...card, padding: 18 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <Mark tone={online ? "good" : "bad"} size={10} />
            <div>
              <div style={{ fontWeight: 700, color: C.ink }}>zigbee2mqtt {online ? "conectado" : "sin conexión"}</div>
              <div style={{ fontSize: 12.5, color: C.ink3 }}>
                {[z.version && `versión ${z.version}`, z.coordinador && z.coordinador.firmware && `coordinador ${z.coordinador.firmware}`, z.last_seen ? (z.last_seen !== "disable" ? "hora real de cada dato activada" : "hora real de cada dato desactivada") : null].filter(Boolean).join(" · ")}
              </div>
            </div>
          </div>
          {isAdmin && (abierto ? (
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <span style={{ fontSize: 14, color: C.primary, fontWeight: 700 }}>Red abierta · quedan {mmss(quedan)}</span>
              <button style={btn(false, true)} disabled={busy} onClick={() => pedir(0)}>Cerrar ahora</button>
            </div>
          ) : (
            <button style={btn(true)} disabled={busy || !online} onClick={() => pedir(120)}><Radio size={15} strokeWidth={1.8} />Permitir emparejar 2 minutos</button>
          ))}
        </div>
        {msg && <div style={{ marginTop: 10, fontSize: 13, color: msg.ok ? C.moss : C.clay, fontWeight: 600 }}>{msg.t}</div>}
        {!isAdmin && <div style={{ marginTop: 10, fontSize: 13, color: C.ink3 }}>Emparejar equipos nuevos lo hace EasyRural.</div>}
      </div>

      <div style={{ ...card, padding: "4px 18px" }}>
        {pasos.map(([t, d], i) => (
          <div key={t} style={{ display: "flex", gap: 14, padding: "14px 0", borderTop: i ? `1px solid ${C.line}` : "none" }}>
            <div style={{ ...mono, fontSize: 12, color: C.ink4, width: 22, paddingTop: 2 }}>{String(i + 1).padStart(2, "0")}</div>
            <div><div style={{ fontWeight: 600, color: C.ink, marginBottom: 3 }}>{t}</div><div style={{ fontSize: 13.5, color: C.ink2, lineHeight: 1.55 }}>{d}</div></div>
          </div>
        ))}
      </div>

      <div style={{ ...card, padding: 18 }}>
        <Eyebrow style={{ marginBottom: 10 }}>Actividad de los últimos 30 minutos</Eyebrow>
        {eventos.length === 0 ? <div style={{ fontSize: 13.5, color: C.ink3 }}>{abierto ? "Esperando equipos…" : "Sin actividad. Abre la red para emparejar un equipo nuevo."}</div> : eventos.map((e, i) => (
          <div key={e.ts + "-" + i} style={{ display: "flex", justifyContent: "space-between", gap: 12, padding: "8px 0", borderTop: i ? `1px solid ${C.line}` : "none", fontSize: 13.5 }}>
            <span><b style={{ color: C.ink }}>{e.equipo || "Equipo"}</b>{e.modelo ? <span style={{ color: C.ink3 }}> · {e.modelo}</span> : null}<span style={{ color: C.ink2 }}> · {EVENTO[e.tipo] || e.tipo}{e.estado === "successful" ? ", listo" : e.estado === "failed" ? ", ha fallado (vuelve a intentarlo)" : ""}</span></span>
            <span style={{ ...mono, color: C.ink3, whiteSpace: "nowrap" }}>{hace2(e.ts)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Página ──────────────────────────────────────────────────────────────────
export default function InstallPage({ acc, isAdmin, isMobile }) {
  const { data, error, refetch } = useApi(() => api.getInstall(acc.locationID), [acc.locationID], { pollMs: 10000 });
  const [view, setView] = useState("asistente");
  const [tick, setTick] = useState(0);
  React.useEffect(() => { const t = setInterval(() => setTick(x => x + 1), 5000); return () => clearInterval(t); }, []);
  const seg = useMemo(() => (data ? Math.max(0, Math.round((Date.now() - Date.parse(data.generado)) / 1000)) : null), [data, tick]); // eslint-disable-line

  const plan = (acc.plan_contratado || acc.plan || "").toLowerCase();
  if (plan && plan !== "completo") {
    return (
      <div style={{ padding: 32 }}>
        <Eyebrow>Instalación</Eyebrow>
        <h2 style={{ fontFamily: "var(--font-serif)", fontSize: 34, fontWeight: 500, color: C.ink, margin: "6px 0" }}>Sin equipos instalados</h2>
        <p style={{ color: C.ink3, maxWidth: 560 }}>Este alojamiento está en el Plan Básico: los datos llegan de Datadis, AEMET y Red Eléctrica, sin aparatos en la casa. Para medir en tiempo real y controlar válvulas o relés hace falta el Plan Completo.</p>
      </div>
    );
  }
  if (!data) {
    return (
      <div style={{ padding: 32, color: error ? C.clay : C.ink3 }}>
        {error ? `No se pudo cargar la instalación: ${error}` : "Cargando la instalación…"}
      </div>
    );
  }

  const devSel = view.startsWith("dev:") ? data.devices.find(d => d.device === view.slice(4)) : null;
  const penSel = view.startsWith("pend:") ? data.pendientes.find(p => String(p.idx) === view.slice(5)) : null;
  const pas = data.devices.filter(d => d.tipo === "pasarela");
  const eqs = data.devices.filter(d => d.tipo !== "pasarela");

  const NavItem = ({ id, icon: Ico, label, right, tone, dashed, indent }) => {
    const act = view === id;
    return (
      <button onClick={() => setView(id)} className="er-install-nav" aria-current={act ? "page" : undefined}
        style={{ position: "relative", width: "100%", display: "flex", alignItems: "center", gap: 10, padding: indent ? "7px 12px 7px 16px" : "9px 12px",
          border: "none", background: act ? "#EBF2E4" : "transparent", borderRadius: 10, cursor: "pointer", textAlign: "left",
          color: act ? C.ink : C.ink2, fontFamily: "var(--font-sans)", fontSize: 13.5, fontWeight: act ? 600 : 450 }}>
        {act && <motion.span layoutId="install-nav" style={{ position: "absolute", left: 0, top: 6, bottom: 6, width: 2, background: C.primary, borderRadius: 1 }} />}
        {Ico ? <Ico size={16} strokeWidth={1.7} style={{ flexShrink: 0 }} /> : <Mark tone={tone} dashed={dashed} />}
        <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{label}</span>
        {right !== undefined && <span style={{ ...mono, fontSize: 11, color: C.ink3 }}>{right}</span>}
      </button>
    );
  };
  const Group = ({ children }) => <Eyebrow style={{ padding: "14px 12px 6px", fontSize: 10 }}>{children}</Eyebrow>;

  const opciones = [["asistente", "Asistente"], ["resumen", "Resumen"],
    ...data.devices.map(d => ["dev:" + d.device, "Equipo · " + nombreDe(d)]), ...data.pendientes.map(p => ["pend:" + p.idx, "Pendiente · " + p.nombre]),
    ["emparejar", "Emparejar Zigbee"], ["conexion", "Pasarela y conexión"], ["ajustes", "Ajustes"]];
  const titulo = devSel ? null : penSel ? null : { asistente: "Asistente de instalación", resumen: "Resumen", emparejar: "Emparejar equipos Zigbee", conexion: "Pasarela y conexión", ajustes: "Ajustes" }[view];

  return (
    <div style={{ padding: isMobile ? 14 : 32 }}>
      <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 16, flexWrap: "wrap", marginBottom: 20 }}>
        <div>
          <Eyebrow>Plan Completo · instalación</Eyebrow>
          <h2 style={{ fontFamily: "var(--font-serif)", fontSize: isMobile ? 28 : 34, fontWeight: 700, color: C.primary, margin: "6px 0 4px", letterSpacing: 0 }}>Instalación</h2>
          <div style={{ color: C.ink3, fontSize: 14.5 }}>Qué hay en {data.nombre}, si está bien puesto y cómo conectarlo. Se actualiza sola.</div>
        </div>
        <div style={{ minWidth: 220 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 6 }}>
            <Eyebrow>Progreso</Eyebrow>
            <span style={{ fontFamily: "var(--font-serif)", fontSize: 22, color: C.ink }}>{data.resumen.progreso} %</span>
          </div>
          <div style={{ height: 4, background: C.line, borderRadius: 2, overflow: "hidden" }}>
            <motion.div initial={{ width: 0 }} animate={{ width: data.resumen.progreso + "%" }} transition={{ duration: 0.8 }} style={{ height: "100%", background: data.resumen.progreso === 100 ? C.moss : C.primary }} />
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", marginTop: 6, fontSize: 12, color: C.ink3 }}>
            <span style={mono}>{seg !== null ? (seg < 5 ? "actualizado ahora" : seg < 120 ? `actualizado hace ${seg} s` : `actualizado ${hace(Math.round(seg / 60))}`) : ""}</span>
            <button onClick={refetch} style={{ border: "none", background: "transparent", color: C.primary, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 5, fontSize: 12, padding: 0 }}><RefreshCw size={12} strokeWidth={2} />Actualizar</button>
          </div>
        </div>
      </div>

      <div className="er-install" style={{ ...card, display: isMobile ? "block" : "grid", gridTemplateColumns: "250px minmax(0,1fr)", minHeight: 560, overflow: "hidden" }}>
        {isMobile ? (
          <div style={{ padding: 12, borderBottom: `1px solid ${C.line}`, background: C.paper }}>
            <select value={view} onChange={e => setView(e.target.value)} style={input} aria-label="Sección">
              {opciones.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
        ) : (
          <aside style={{ borderRight: `1px solid ${C.line}`, background: "#FAFAFA", padding: "8px 8px 16px", overflowY: "auto" }}>
            <Group>Vista general</Group>
            <NavItem id="asistente" icon={ListChecks} label="Asistente" right={data.resumen.progreso + " %"} />
            <NavItem id="resumen" icon={LayoutGrid} label="Resumen" right={data.devices.length} />
            {pas.length > 0 && <Group>Pasarelas</Group>}
            {pas.map(d => <NavItem key={d.device} id={"dev:" + d.device} tone={d.tone} label={nombreDe(d)} indent />)}
            <Group>Equipos · {eqs.length}{data.pendientes.length ? ` + ${data.pendientes.length} por llegar` : ""}</Group>
            {eqs.map(d => <NavItem key={d.device} id={"dev:" + d.device} tone={d.tone} label={nombreDe(d)} right={principal(d) !== "—" ? principal(d) : undefined} indent />)}
            {data.pendientes.map(p => <NavItem key={"p" + p.idx} id={"pend:" + p.idx} dashed label={p.nombre} indent />)}
            {eqs.length === 0 && data.pendientes.length === 0 && <div style={{ fontSize: 12.5, color: C.ink3, padding: "4px 16px" }}>Ninguno todavía</div>}
            <Group>Configuración</Group>
            <NavItem id="emparejar" icon={Radio} label="Emparejar Zigbee" right={data.zigbee && data.zigbee.permit_join ? "abierta" : undefined} />
            <NavItem id="conexion" icon={Cable} label="Pasarela y conexión" />
            <NavItem id="ajustes" icon={Settings2} label="Ajustes" />
          </aside>
        )}
        <section style={{ padding: isMobile ? 14 : 24, minWidth: 0 }}>
          {titulo && <div style={{ fontFamily: "var(--font-serif)", fontSize: 22, fontWeight: 700, color: C.primary, marginBottom: 16 }}>{titulo}</div>}
          {view === "asistente" && <Asistente data={data} open={setView} />}
          {view === "resumen" && <Resumen data={data} open={setView} />}
          {view === "emparejar" && <Emparejar data={data} acc={acc} isAdmin={isAdmin} refetch={refetch} />}
          {view === "conexion" && <Conexion data={data} acc={acc} />}
          {view === "ajustes" && <Ajustes data={data} acc={acc} isAdmin={isAdmin} refetch={refetch} />}
          {devSel && <Equipo key={devSel.device} d={devSel} data={data} isAdmin={isAdmin} acc={acc} refetch={refetch} open={setView} />}
          {penSel && <Pendiente p={penSel} data={data} />}
          {!devSel && !penSel && !titulo && <div style={{ color: C.ink3 }}>Ese equipo ya no está en la lista. <button onClick={() => setView("resumen")} style={{ ...btn(false), marginLeft: 8 }}>Ver resumen</button></div>}
        </section>
      </div>
    </div>
  );
}
